package com.yendo.mx.models

import androidx.compose.runtime.mutableStateListOf
import com.russhwolf.settings.Settings
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import com.yendo.mx.utils.redondear
import kotlin.math.roundToInt

object CartManager {
    private val settings: Settings = Settings()
    private val json = Json { ignoreUnknownKeys = true }
    
    val items = mutableStateListOf<CartItem>()

    // 🚀 Guardamos la información del restaurante del pedido actual
    var restauranteActualId: String? = null
    var restauranteActualIdZona: String? = null // 🚀 NUEVO
    var restauranteActualNombre: String? = null
    var restauranteActualLogo: String? = null // 🚀 NUEVO
    var restauranteActualDireccion: String? = null
    var restauranteActualLat: Double = 0.0
    var restauranteActualLng: Double = 0.0

    // 🚀 Guardamos los métodos de pago y reglas logísticas
    var restauranteActualMetodosPago: List<String>? = null
    var restauranteActualDeliveryActivo: Boolean? = true
    var restauranteActualDeliveryPropioActivo: Boolean? = false
    var restauranteActualPickupActivo: Boolean? = true
    var restauranteActualProgramadoActivo: Boolean? = false

    var restauranteActualBanco: String? = null
    var restauranteActualCuenta: String? = null
    var restauranteActualBeneficiario: String? = null

    // 🚀 Escudo anti-deudas cruzado
    var restauranteActualDeuda: Double? = 0.0
    var restauranteActualLimiteDeuda: Double? = null
    var restauranteActualBalanceExcedido: Boolean? = false

    // 🚀 Control de pánico y pausas
    var restauranteActualPausadoManualmente: Boolean? = false
    var restauranteActualPausaHasta: Long? = 0L

    // 🚀 Guardamos los horarios para que el Carrito y Checkout puedan validarlos
    var restauranteActualHorarios: Horarios? = null

    // 🚀 NUEVO: Guardamos el pedido mínimo del restaurante
    var restauranteActualPedidoMinimo: Double? = 0.0

    // 🚀 NUEVO: Guardamos la comisión del restaurante
    var restauranteActualComision: Double? = 0.0

    // 🚀 NUEVOS CAMPOS PARA COMERCIOS
    var restauranteActualTipoNegocio: String? = "RESTAURANTE"
    var restauranteActualPagoPostArmado: Boolean? = false

    init {
        hidratarCarrito()
    }

    // 🚀 Pregunta si podemos agregar un producto de este restaurante
    fun puedeAgregar(idRestaurante: String): Boolean {
        if (items.isEmpty()) return true
        return restauranteActualId == idRestaurante
    }

    // 🚀 NUEVO: Permite refrescar las reglas del negocio sin vaciar el carrito
    fun sincronizarMetadata(rest: RestauranteFirebase) {
        if (rest.id != restauranteActualId) return
        
        restauranteActualNombre = rest.infoPublica.nombre
        val metodosFinales = rest.metodosPagoAceptadosFinal
        val datosBancarios = rest.datosBancariosFinal

        restauranteActualLogo = rest.infoPublica.logotipo
        restauranteActualDireccion = rest.ubicacion.direccion
        restauranteActualLat = rest.ubicacion.latitud
        restauranteActualLng = rest.ubicacion.longitud
        restauranteActualMetodosPago = metodosFinales
        restauranteActualDeliveryActivo = rest.operacion.deliveryActivo
        restauranteActualDeliveryPropioActivo = rest.operacion.tieneFlotaPropiaActiva
        restauranteActualPickupActivo = rest.operacion.pickupActivo
        restauranteActualProgramadoActivo = rest.operacion.programadoActivo
        restauranteActualBanco = datosBancarios?.banco
        restauranteActualCuenta = datosBancarios?.cuenta
        restauranteActualBeneficiario = datosBancarios?.beneficiario
        restauranteActualDeuda = rest.finanzasLocales.balanceActualDeuda
        restauranteActualLimiteDeuda = rest.finanzasLocales.limiteDeudaPersonalizado
        restauranteActualBalanceExcedido = rest.operacion.balanceExcedido
        restauranteActualPausadoManualmente = rest.operacion.pausadoManualmente
        restauranteActualPausaHasta = rest.operacion.pausaHasta
        restauranteActualHorarios = rest.horarios
        restauranteActualPedidoMinimo = rest.operacion.montoMinimoCompra.toDouble()
        restauranteActualComision = rest.finanzasLocales.comisionPersonalizada.toDouble()
        restauranteActualTipoNegocio = rest.infoPublica.tipoNegocio
        restauranteActualPagoPostArmado = rest.operacion.pagoPostArmado

        println("YENDO_LOG CART: Metadata sincronizada para ${rest.id}. metodosPagoAceptados = ${rest.finanzasLocales.metodosPagoAceptados}, banco = '${rest.finanzasLocales.datosBancarios?.banco}', cuenta = '${rest.finanzasLocales.datosBancarios?.cuenta}'")
        
        persistirCarrito()
    }

    /**
     * 🚀 NUEVO: Sincroniza los precios y escalas de los productos actuales
     */
    fun sincronizarProductos(listaNuevos: List<Producto>) {
        if (items.isEmpty()) return
        
        var huboCambios = false
        items.forEachIndexed { index, itemExistente ->
            listaNuevos.find { it.id == itemExistente.producto.id }?.let { prodActualizado ->
                // 🚀 ACTUALIZAMOS PRECIOS DE ADICIONALES SELECCIONADOS
                val adicionalesActualizados = itemExistente.adicionales.map { adicViejo ->
                    // Buscamos el adicional dentro del nuevo objeto producto
                    val adicFresco = prodActualizado.adicionales
                        .flatMap { it.opciones }
                        .find { it.nombre == adicViejo.nombre }
                    
                    adicFresco ?: adicViejo
                }

                // Solo actualizamos si algo cambió (Escalas, precio base, disponibilidad o adicionales)
                if (itemExistente.producto != prodActualizado || itemExistente.adicionales != adicionalesActualizados) {
                    items[index] = itemExistente.copy(
                        producto = prodActualizado,
                        adicionales = adicionalesActualizados
                    )
                    huboCambios = true
                }
            }
        }
        
        if (huboCambios) {
            println("YENDO_LOG: Se detectaron y aplicaron cambios en productos/adicionales del carrito.")
            persistirCarrito()
        }
    }

    fun agregarProducto(
        producto: Producto,
        idRestaurante: String? = null,
        idZona: String? = null, // 🚀 NUEVO
        nombreRestaurante: String? = null,
        logoRestaurante: String? = null, // 🚀 NUEVO
        direccionRestaurante: String? = null,
        lat: Double = 0.0,
        lng: Double = 0.0,
        metodosPagoAceptados: List<String>? = null,
        deliveryActivo: Boolean? = true,
        pickupActivo: Boolean? = true,
        programadoActivo: Boolean? = false,
        bancoRestaurante: String? = null,
        cuentaRestaurante: String? = null,
        beneficiarioRestaurante: String? = null,
        deudaRestaurante: Double? = 0.0,
        limiteDeudaRestaurante: Double? = null,
        balanceExcedidoRestaurante: Boolean? = false,
        pausadoManualmente: Boolean? = false,
        pausaHasta: Long? = 0L,
        horarios: Horarios? = null,
        pedidoMinimoRestaurante: Double? = 0.0,
        // 🚀 NUEVO PARÁMETRO: Comisión
        comisionRestaurante: Double? = 0.0,
        tipoNegocio: String? = "RESTAURANTE",
        pagoPostArmado: Boolean? = false
    ) {
        if ((items.isEmpty() || restauranteActualId == idRestaurante) && idRestaurante != null) {
            restauranteActualId = idRestaurante
            restauranteActualIdZona = idZona // 🚀 FIX: Asignamos el ID de zona al iniciar carrito
            restauranteActualNombre = nombreRestaurante
            restauranteActualLogo = logoRestaurante
            restauranteActualDireccion = direccionRestaurante
            restauranteActualLat = lat
            restauranteActualLng = lng
            restauranteActualMetodosPago = metodosPagoAceptados
            restauranteActualDeliveryActivo = deliveryActivo
            restauranteActualPickupActivo = pickupActivo
            restauranteActualProgramadoActivo = programadoActivo

            restauranteActualBanco = bancoRestaurante
            restauranteActualCuenta = cuentaRestaurante
            restauranteActualBeneficiario = beneficiarioRestaurante

            restauranteActualDeuda = deudaRestaurante
            restauranteActualLimiteDeuda = limiteDeudaRestaurante
            restauranteActualBalanceExcedido = balanceExcedidoRestaurante

            restauranteActualPausadoManualmente = pausadoManualmente
            restauranteActualPausaHasta = pausaHasta
            restauranteActualHorarios = horarios

            // 🚀 GUARDAMOS EL MÍNIMO Y LA COMISIÓN AL CREAR EL CARRITO
            restauranteActualPedidoMinimo = pedidoMinimoRestaurante
            restauranteActualComision = comisionRestaurante
            restauranteActualTipoNegocio = tipoNegocio
            restauranteActualPagoPostArmado = pagoPostArmado
        }

        val cantidadIncremento = if (producto.tipoVenta == "PESO") {
            if (producto.pesoEstimadoKg > 0.0) producto.pesoEstimadoKg else 0.25
        } else {
            1.0
        }
        val modoCompraCalculado = if (producto.tipoVenta == "PESO") "PESO" else "UNIDAD"

        val indice = items.indexOfFirst {
            it.producto.id == producto.id && it.adicionales.isEmpty() && it.notas.isEmpty() && it.modoCompra == modoCompraCalculado
        }

        if (indice != -1) {
            val itemActual = items[indice]
            val nuevaCant = ((itemActual.cantidad + cantidadIncremento) * 1000.0).roundToInt() / 1000.0
            items[indice] = itemActual.copy(cantidad = nuevaCant)
        } else {
            items.add(CartItem(producto, cantidadIncremento, modoCompra = modoCompraCalculado))
        }
        persistirCarrito()
    }

    fun agregarAlCarrito(
        producto: Producto,
        cantidad: Double,
        notas: String = "",
        adicionales: List<OpcionAdicional> = emptyList(),
        modoCompra: String = "UNIDAD",
        idRestaurante: String? = null,
        idZona: String? = null, // 🚀 NUEVO
        nombreRestaurante: String? = null,
        logoRestaurante: String? = null, // 🚀 NUEVO
        direccionRestaurante: String? = null,
        lat: Double = 0.0,
        lng: Double = 0.0,
        metodosPagoAceptados: List<String>? = null,
        deliveryActivo: Boolean? = true,
        deliveryPropioActivo: Boolean? = false,
        pickupActivo: Boolean? = true,
        programadoActivo: Boolean? = false,
        bancoRestaurante: String? = null,
        cuentaRestaurante: String? = null,
        beneficiarioRestaurante: String? = null,
        deudaRestaurante: Double? = 0.0,
        limiteDeudaRestaurante: Double? = null,
        balanceExcedidoRestaurante: Boolean? = false,
        pausadoManualmente: Boolean? = false,
        pausaHasta: Long? = 0L,
        horarios: Horarios? = null,
        pedidoMinimoRestaurante: Double? = 0.0,
        // 🚀 NUEVO PARÁMETRO: Comisión
        comisionRestaurante: Double? = 0.0,
        tipoNegocio: String? = "RESTAURANTE",
        pagoPostArmado: Boolean? = false
    ) {
        // 🚀 ADUANA ABSOLUTA ANTI-CRUCE: Si el carrito pertenece a otro negocio, rechaza la inserción
        if (items.isNotEmpty() && restauranteActualId != null && restauranteActualId != idRestaurante) {
            println("🚨 CART ESCUDO: Rechazada inserción. Carrito pertenece a '$restauranteActualId' y se intentó agregar de '$idRestaurante'")
            return
        }

        if ((items.isEmpty() || restauranteActualId == idRestaurante) && idRestaurante != null) {
            restauranteActualId = idRestaurante
            restauranteActualNombre = nombreRestaurante
            restauranteActualLogo = logoRestaurante
            restauranteActualDireccion = direccionRestaurante
            restauranteActualLat = lat
            restauranteActualLng = lng
            restauranteActualMetodosPago = metodosPagoAceptados
            restauranteActualDeliveryActivo = deliveryActivo
            restauranteActualDeliveryPropioActivo = deliveryPropioActivo
            restauranteActualPickupActivo = pickupActivo
            restauranteActualProgramadoActivo = programadoActivo

            restauranteActualBanco = bancoRestaurante
            restauranteActualCuenta = cuentaRestaurante
            restauranteActualBeneficiario = beneficiarioRestaurante

            restauranteActualDeuda = deudaRestaurante
            restauranteActualLimiteDeuda = limiteDeudaRestaurante
            restauranteActualBalanceExcedido = balanceExcedidoRestaurante

            restauranteActualPausadoManualmente = pausadoManualmente
            restauranteActualPausaHasta = pausaHasta
            restauranteActualHorarios = horarios

            // 🚀 GUARDAMOS EL MÍNIMO Y LA COMISIÓN AL CREAR EL CARRITO
            restauranteActualPedidoMinimo = pedidoMinimoRestaurante
            restauranteActualComision = comisionRestaurante
            restauranteActualTipoNegocio = tipoNegocio
            restauranteActualPagoPostArmado = pagoPostArmado
        }

        val indice = items.indexOfFirst {
            it.producto.id == producto.id &&
                    it.notas == notas &&
                    it.adicionales == adicionales &&
                    it.modoCompra == modoCompra
        }

        if (indice != -1) {
            val itemActual = items[indice]
            items[indice] = itemActual.copy(cantidad = itemActual.cantidad + cantidad)
        } else {
            items.add(CartItem(producto, cantidad, notas, adicionales, modoCompra))
        }
        persistirCarrito()

        // 🚀 ANALYTICS: REGISTRAR ADD_TO_CART EN GA4
        try {
            com.yendo.mx.utils.AdAnalyticsManager.logAddToCart(
                productId = producto.id,
                productName = producto.nombre,
                price = producto.precio,
                quantity = cantidad.toInt(),
                merchantName = restauranteActualNombre ?: ""
            )
        } catch (e: Exception) {}
    }

    fun actualizarCantidad(item: CartItem, nuevaCantidad: Double) {
        val index = items.indexOf(item)
        if (index != -1) {
            items[index] = item.copy(cantidad = nuevaCantidad)
            persistirCarrito()
        }
    }

    fun eliminarItem(item: CartItem) {
        if (items.remove(item)) {
            if (items.isEmpty()) resetRestaurante()
            persistirCarrito()
        }
    }

    fun removerItemExacto(itemExacto: CartItem) {
        val index = items.indexOf(itemExacto)
        if (index != -1) {
            val itemActual = items[index]
            val step = if (itemActual.producto.tipoVenta == "PESO") {
                if (itemActual.producto.pesoEstimadoKg > 0.0) itemActual.producto.pesoEstimadoKg else 0.25
            } else {
                1.0
            }
            if (itemActual.cantidad > step + 0.001) {
                val nuevaCant = ((itemActual.cantidad - step) * 1000.0).roundToInt() / 1000.0
                items[index] = itemActual.copy(cantidad = nuevaCant)
            } else {
                items.removeAt(index)
                if (items.isEmpty()) {
                    resetRestaurante()
                }
            }
            persistirCarrito()
        }
    }

    fun removerProducto(producto: Producto) {
        val index = items.indexOfLast { it.producto.id == producto.id }
        if (index != -1) {
            val itemActual = items[index]
            val step = if (itemActual.producto.tipoVenta == "PESO") {
                if (itemActual.producto.pesoEstimadoKg > 0.0) itemActual.producto.pesoEstimadoKg else 0.25
            } else {
                1.0
            }
            if (itemActual.cantidad > step + 0.001) {
                val nuevaCant = ((itemActual.cantidad - step) * 1000.0).roundToInt() / 1000.0
                items[index] = itemActual.copy(cantidad = nuevaCant)
            } else {
                items.removeAt(index)
                if (items.isEmpty()) resetRestaurante()
            }
            persistirCarrito()
        }
    }

    private fun resetRestaurante() {
        restauranteActualId = null
        restauranteActualIdZona = null
        restauranteActualNombre = null
        restauranteActualLogo = null // 🚀 LIMPIAMOS EL LOGO
        restauranteActualDireccion = null
        restauranteActualLat = 0.0
        restauranteActualLng = 0.0
        restauranteActualMetodosPago = null
        restauranteActualDeliveryActivo = true
        restauranteActualPickupActivo = true
        restauranteActualProgramadoActivo = false
        restauranteActualBanco = null
        restauranteActualCuenta = null
        restauranteActualBeneficiario = null
        restauranteActualDeuda = 0.0
        restauranteActualLimiteDeuda = null
        restauranteActualBalanceExcedido = false
        restauranteActualPausadoManualmente = false
        restauranteActualPausaHasta = 0L
        restauranteActualHorarios = null
        restauranteActualPedidoMinimo = 0.0
        restauranteActualComision = 0.0 // 🚀 LIMPIAMOS LA COMISIÓN
        restauranteActualTipoNegocio = "RESTAURANTE"
        restauranteActualPagoPostArmado = false
    }

    fun vaciarCarrito() {
        items.clear()
        resetRestaurante()
        persistirCarrito()
    }

    private fun persistirCarrito() {
        try {
            val itemsJson = json.encodeToString(items.toList())
            settings.putString("cart_items", itemsJson)

            val rest = RestaurantePersistido(
                id = restauranteActualId,
                idZona = restauranteActualIdZona,
                nombre = restauranteActualNombre,
                logo = restauranteActualLogo, // 🚀 PERSISTIMOS EL LOGO
                direccion = restauranteActualDireccion,
                lat = restauranteActualLat,
                lng = restauranteActualLng,
                metodosPago = restauranteActualMetodosPago,
                deliveryActivo = restauranteActualDeliveryActivo,
                pickupActivo = restauranteActualPickupActivo,
                programadoActivo = restauranteActualProgramadoActivo,
                banco = restauranteActualBanco,
                cuenta = restauranteActualCuenta,
                beneficiario = restauranteActualBeneficiario,
                deuda = restauranteActualDeuda,
                limiteDeuda = restauranteActualLimiteDeuda,
                balanceExcedido = restauranteActualBalanceExcedido,
                pausadoManualmente = restauranteActualPausadoManualmente,
                pausaHasta = restauranteActualPausaHasta,
                horarios = restauranteActualHorarios,
                pedidoMinimo = restauranteActualPedidoMinimo,
                comision = restauranteActualComision,
                tipoNegocio = restauranteActualTipoNegocio,
                pagoPostArmado = restauranteActualPagoPostArmado
            )
            val restJson = json.encodeToString(rest)
            settings.putString("cart_restaurant", restJson)
        } catch (e: Exception) {
            println("🚨 Error persistiendo carrito: ${e.message}")
        }
    }

    private fun hidratarCarrito() {
        try {
            val itemsJson = settings.getStringOrNull("cart_items")
            if (itemsJson != null) {
                val savedItems = json.decodeFromString<List<CartItem>>(itemsJson)
                items.clear()
                items.addAll(savedItems)
            }

            val restJson = settings.getStringOrNull("cart_restaurant")
            if (restJson != null) {
                val rest = json.decodeFromString<RestaurantePersistido>(restJson)
                restauranteActualId = rest.id
                restauranteActualIdZona = rest.idZona
                restauranteActualNombre = rest.nombre
                restauranteActualLogo = rest.logo // 🚀 HIDRATAMOS EL LOGO
                restauranteActualDireccion = rest.direccion
                restauranteActualLat = rest.lat
                restauranteActualLng = rest.lng
                restauranteActualMetodosPago = rest.metodosPago
                restauranteActualDeliveryActivo = rest.deliveryActivo
                restauranteActualPickupActivo = rest.pickupActivo
                restauranteActualProgramadoActivo = rest.programadoActivo
                restauranteActualBanco = rest.banco
                restauranteActualCuenta = rest.cuenta
                restauranteActualBeneficiario = rest.beneficiario
                restauranteActualDeuda = rest.deuda
                restauranteActualLimiteDeuda = rest.limiteDeuda
                restauranteActualBalanceExcedido = rest.balanceExcedido
                restauranteActualPausadoManualmente = rest.pausadoManualmente
                restauranteActualPausaHasta = rest.pausaHasta
                restauranteActualHorarios = rest.horarios
                restauranteActualPedidoMinimo = rest.pedidoMinimo
                restauranteActualComision = rest.comision
                restauranteActualTipoNegocio = rest.tipoNegocio
                restauranteActualPagoPostArmado = rest.pagoPostArmado
            }
        } catch (e: Exception) {
            println("🚨 Error hidratando carrito: ${e.message}")
        }
    }

    val cantidadTotal: Double get() = items.sumOf { it.cantidad }
    val totalArticulosBadge: Int get() = items.sumOf { item ->
        if (item.producto.tipoVenta == "PESO" || item.producto.tipoVenta == "PIEZA_POR_PESO") {
            1
        } else {
            item.cantidad.toInt()
        }
    }
    val totalDinero: Double get() = items.sumOf { it.precioTotal }
}

@Serializable
data class RestaurantePersistido(
    val id: String?,
    val idZona: String? = null, // 🚀 NUEVO
    val nombre: String?,
    val logo: String? = null, // 🚀 NUEVO
    val direccion: String?,
    val lat: Double,
    val lng: Double,
    val metodosPago: List<String>?,
    val deliveryActivo: Boolean?,
    val pickupActivo: Boolean?,
    val programadoActivo: Boolean?,
    val banco: String?,
    val cuenta: String?,
    val beneficiario: String?,
    val deuda: Double?,
    val limiteDeuda: Double?,
    val balanceExcedido: Boolean?,
    val pausadoManualmente: Boolean?,
    val pausaHasta: Long?,
    val horarios: Horarios?,
    val pedidoMinimo: Double?,
    val comision: Double?,
    val tipoNegocio: String?,
    val pagoPostArmado: Boolean?
)

@Serializable
data class CartItem(
    val producto: Producto,
    val cantidad: Double,
    val notas: String = "",
    val adicionales: List<OpcionAdicional> = emptyList(),
    val modoCompra: String = "UNIDAD"
) {
    val precioUnitarioVigente: Double get() {
        // 1. Calculamos la cantidad total convirtiendo a Kilos ciegamente
        val qTotal = CartManager.items
            .filter { it.producto.id == this.producto.id }
            .sumOf { item ->
                if (item.producto.tipoVenta == "PIEZA_POR_PESO") {
                    // 🚀 FIX: Siempre es Piezas * Peso Estimado
                    item.cantidad * item.producto.pesoEstimadoKg
                } else {
                    item.cantidad
                }
            }

        val escala = producto.escalasPrecio
            .filter { qTotal >= it.cantidadMinima.toDouble() && it.precioUnitario > 0.0 }
            .maxByOrNull { it.cantidadMinima }

        return if (escala != null) {
            val qMax = if (escala.cantidadMaxima.toDouble() > 0.0) {
                escala.cantidadMaxima.toDouble()
            } else {
                Double.MAX_VALUE
            }

            if (qTotal > qMax) {
                val costoConMayoreo = qMax * escala.precioUnitario
                val costoExcedenteRegular = (qTotal - qMax) * producto.precio
                (costoConMayoreo + costoExcedenteRegular) / qTotal
            } else {
                escala.precioUnitario
            }
        } else {
            producto.precio
        }
    }

    val precioExtras: Double get() = adicionales.sumOf { it.precioExtra }

    val precioTotal: Double get() = redondear(when (producto.tipoVenta) {
        "PIEZA_POR_PESO" -> {
            // 🚀 FIX: MATEMÁTICA PURA (Piezas * Peso Estimado) * PrecioVigente
            val pesoFisicoEstimado = cantidad * producto.pesoEstimadoKg
            (pesoFisicoEstimado * precioUnitarioVigente) + (precioExtras * cantidad)
        }
        "PESO" -> {
            (cantidad * precioUnitarioVigente) + (precioExtras * cantidad)
        }
        else -> {
            (cantidad * precioUnitarioVigente) + (precioExtras * cantidad)
        }
    })
}
